import type { RequestLogEntry } from "@/types/dashboard";

export function escapeHtml(s: string): string {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function fmtWhen(ts: number): string {
  if (!ts) return "—";
  const d = new Date(ts);
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

export function fmtDateTime(ts: number): string {
  if (!ts) return "—";
  try {
    return new Date(ts).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false
    });
  } catch {
    return "—";
  }
}

export function fmtDateTimeShort(ts: number): string {
  if (!ts) return "—";
  try {
    const d = new Date(ts);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) return fmtWhen(ts);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false
    });
  } catch {
    return "—";
  }
}

export function fmtDeployVerRelative(ms: number): string {
  const t = Number(ms) || 0;
  if (!t) return "";
  const ago = Date.now() - t;
  const m = Math.floor(ago / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export function fmtPageStatPct(count: number, total: number): string {
  const c = Number(count) || 0;
  const t = Number(total) || 0;
  if (!t) return "0%";
  return `${Math.round((c / t) * 1000) / 10}%`;
}

export function fmtCredits(n: number): string {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(2).replace(/\.?0+$/, "")}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1).replace(/\.?0+$/, "")}k`;
  return String(Math.round(v * 100) / 100);
}

export function truncateText(s: string, max: number): string {
  const t = String(s ?? "");
  if (t.length <= max) return t;
  return t.slice(0, Math.max(0, max - 1)) + "…";
}

export function fmtBytes(n: number): string {
  const v = Number(n) || 0;
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`;
  if (v < 1024 * 1024 * 1024) return `${(v / 1024 / 1024).toFixed(1)} MB`;
  return `${(v / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

export function statusKind(status: number): "good" | "warn" | "bad" | "" {
  if (status >= 200 && status < 300) return "good";
  if (status === 304) return "warn";
  if (status >= 400 && status < 500) return "warn";
  if (status >= 500) return "bad";
  return "";
}

export function logSourceBucket(e: RequestLogEntry): string {
  const sb = String(e.sourceBucket || "");
  if (sb === "browser_cache" || e.source === "browser_cache") return "browser_cache";
  if (["nginx", "node", "origin"].includes(sb)) return sb;
  const src = String(e.source || "");
  if (src === "browser_cache") return "browser_cache";
  if (src === "cache" || src === "cache_staging") return "node";
  if (src === "nginx_mirror") return "nginx";
  if (src.startsWith("upstream")) return "origin";
  return src ? "node" : "";
}

export function logSourceLabel(bucket: string): string {
  if (bucket === "browser_cache") return "Browser cache";
  if (bucket === "node") return "SW App";
  if (bucket === "nginx") return "SW Edge";
  if (bucket === "origin") return "Origin";
  if (bucket === "cloudfront") return "CloudFront";
  return bucket || "—";
}
